import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import * as Sentry from "@sentry/react";
import { Box, Alert, Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography } from "@mui/material";
import { buildQuickSearchQuery, TemplateQueryProps } from "components/widgets/utility/TemplateSearch/TemplateSearchContent";
import APIRequest, { APIError, enqueueAPIErrorSnackbar } from "system/APIRequest";
import SavedQueryDialog from "components/queries/savedQueries/SavedQueryDialog";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import WidgetMultiqueryPanel from "./WidgetMultiqueryPanel";
import { WidgetQuery } from "system/widget/Widget";
import { Query } from "system/query/Query";
import { optimizeQueries } from "system/query/QueryUtility";
import { currentUser, InfegyUser, UserPreference } from "system/InfegyUser";

export interface WidgetMultiqueryDialogProps {
  queryIds: string[];

  // By default, all widget queries must use the same dataset.
  // Very few widgets make sense with multiple datasets, and it can provide
  // confusion to the user, so use this sparingly.
  allowUnmatchedDatasets?: boolean;

  // If a pre-defined dataset is to be used, for example when using a social only widget
  predefinedDatasetId?: string;

  // Allow the user to quickly select the dashboard dataset if available
  dashboardDatasetId?: string;

  onClose: () => void;
  onSubmit: (widgetQueries: WidgetQuery[]) => void;
  canSubmitEmptyForm?: boolean;
}

export const saveWidgetQueries = async (widgetQueries: TemplateQueryProps[], fixedDatasetId: string | undefined, user: InfegyUser | undefined, setQueryInfoError: Function) => {

  try {
    // Only load valid queries
    const validTemplateQueries = widgetQueries.filter((templateQuery) => {
      // filter out blank queries
      return !!templateQuery.quickSearchContent || templateQuery.savedQueryId;
    });

    // Separate out quick search queries that need to be created and build them
    const quickSearchQueries = validTemplateQueries.filter((templateQuery) => {
      // filter out saved queries so we only have queries that need to be saved
      return !!templateQuery.quickSearchContent && !templateQuery.savedQueryId && !templateQuery.usingAIQueryBuilder;
    }).map((templateQuery) => {
      return buildQuickSearchQuery(templateQuery.queryTitle, templateQuery.quickSearchContent, templateQuery.searchFieldIds, templateQuery.selectedDatasetId || templateQuery.queryInfo?.dataset?.id || fixedDatasetId || "");
    }).filter((query) => { return !!query; }) as Query[];

    // Run optimization on all quick search queries to convert them to builder queries and identify any issues
    const optimizedQueries = await optimizeQueries(quickSearchQueries, user?.getPreference(UserPreference.SKIP_CONVERTING_QUICKSEARCH_BOOLEAN) === "on");

    const mergedOptimizedQueries = optimizedQueries.map((optimizedQuery, index) => {
      const templateQuery = quickSearchQueries[index];
      return { ...templateQuery, ...optimizedQuery };
    });

    const aiPromptEntries = validTemplateQueries.filter((templateQuery) => {
      // filter out saved queries so we only have queries that need to be saved
      const content = templateQuery?.quickSearchContent?.trim() || "";
      return content.length && !!templateQuery.quickSearchContent && !templateQuery.savedQueryId && templateQuery.usingAIQueryBuilder;
    });

    const aiGenPromises = aiPromptEntries.map((templateQuery) => {
        return APIRequest(`query/ai-assist`, {
          prompt: templateQuery.quickSearchContent
        });
    });

    const gennedAIQueries = await Promise.all(aiGenPromises);

    const aiQueries = gennedAIQueries.map((aiQuery, index) => {
      const content = aiPromptEntries[index].quickSearchContent.trim();
      return new Query({
        dataset_id: fixedDatasetId,
        query: aiQuery,
        prompt: content,
        name: content.slice(0, 150)
      });
    });

    // Create requests for each quick search query
    const queryAPIPromises = mergedOptimizedQueries.concat(aiQueries).map((quickSearchQuery) => {
      return APIRequest("obj/query", {}, {
        method: "POST",
        "body": JSON.stringify(quickSearchQuery, undefined , 2)
      });
    });

    // Wait for all the API requests to finish
    const queryAPIResponses = await Promise.all(queryAPIPromises);

    // Check if any of the API requests failed
    const anyFailed = queryAPIResponses.some(response => !response.id);

    // If none of the API requests failed, return the processed queries
    if (!anyFailed) {
      // Build new WidgetQueries from the response and existing saved queries
      let lastResponseIndex = 0;
      const savedWidgetQueries: WidgetQuery[] = validTemplateQueries.map((templateQuery) => {
        if (!templateQuery.savedQueryId) {
          const queryResponse = queryAPIResponses[lastResponseIndex++];
          return {
            name: queryResponse?.name,
            id: queryResponse?.id
          } as WidgetQuery;
        }
        return {
          name: templateQuery.queryTitle || "",
          id: templateQuery.savedQueryId
        } as WidgetQuery;
      });

      return savedWidgetQueries;
    } else {
      // If any requests failed, throw an error
      throw new Error('One or more query API requests failed');
    }
  } catch (error) {
    setQueryInfoError(error as APIError);
  }
}


export default function WidgetMultiqueryDialog(props: WidgetMultiqueryDialogProps): JSX.Element {
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const user = currentUser();

  const { queryIds, allowUnmatchedDatasets, predefinedDatasetId, onClose, onSubmit } = props;

  const [queries, setQueries] = useState<TemplateQueryProps[]>([]);
  const [useDashboardDataset, setUseDashboardDataset] = useState<boolean>(!predefinedDatasetId && !!props.dashboardDatasetId);

  const [loadQueryDialogIsOpen, setLoadQueryDialogIsOpen] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<APIError | undefined>(undefined);

  const maxWidgetQueries = 10;

  // fetch all queryInfos for a set of query IDs
  const retrieveQueryInfos = async (queries: TemplateQueryProps[]) => {
    // if the query already has queryInfo or cannot fetch one because of a lacking savedQueryId, skip it
    const promises = queries.map((query) => {
      if (query.queryInfo || !query.savedQueryId) {
        return Promise.resolve(query);
      }
      return APIRequest(`query-info/${query.savedQueryId}`, {}, {
        method: 'GET',
      }).then((response) => {
        const newTemplateQuery = Object.assign(new TemplateQueryProps(user), query);
        newTemplateQuery.queryInfo = response;
        newTemplateQuery.selectedDatasetId = response?.dataset?.id;
        return newTemplateQuery;
      });
    });
    return await Promise.all(promises);
  };

  useEffect(() => {
    // Fetch the actual query data from the API based on the query IDs
    if (!queryIds || queryIds?.length === 0) {
      setIsLoading(false);
      setError(undefined);
      setQueries([
        new TemplateQueryProps(user)
      ]);
      return;
    }

    setError(undefined);

    // Create TemplateQueryProps from the queryIds
    const templateQueries: TemplateQueryProps[] = queryIds.map((queryId: string) => {
      const newTemplateQuery = new TemplateQueryProps(user);
      newTemplateQuery.savedQueryId = queryId;
      newTemplateQuery.usingAIQueryBuilder = false;
      return newTemplateQuery;
    });
    setQueries(templateQueries);
  }, [queryIds, allowUnmatchedDatasets, predefinedDatasetId, i18n, enqueueSnackbar]);

  useEffect(() => {
    if (!queries || !queries.find(q => q.savedQueryId && !q.queryInfo)) {
      return;
    }
    setIsLoading(true);
    setError(undefined);

    // Fetch queryInfos for all queries that don't have them yet
    retrieveQueryInfos(queries).then((responses: any) => {
      setQueries([...responses]);
      setError(undefined);
      setIsLoading(false);
    })
    .catch((apiError: APIError) => {
      setError(apiError);
      setIsLoading(false);
      enqueueAPIErrorSnackbar(apiError, enqueueSnackbar);
    });
  }, [i18n, enqueueSnackbar, queries, allowUnmatchedDatasets, predefinedDatasetId]);

  function commitQueries() {
    // Run AI Queries, wait for a response, then call onSubmit with the new queries
    setIsLoading(true);
    setError(undefined);
    try {
      saveWidgetQueries(queries, predefinedDatasetId || fixedDatasetId || props.dashboardDatasetId, user, setError).then((savedTemplateQueries) => {
        setIsLoading(false);
        if (savedTemplateQueries) {
          onSubmit(savedTemplateQueries as WidgetQuery[]);
        }
      }).catch((error) => {
        setIsLoading(false);
        setError(error);
        enqueueAPIErrorSnackbar(error, enqueueSnackbar);
      });
    } catch (error) {
      setIsLoading(false);
      setError(error);
      console.error("Error saving widget queries:", error);
    }
  }

  // If a query is using a dataset other than the dashboard dataset, disable the dashboard dataset option
  const dashboardDatasetOptionIsDisabled = useMemo(() => {
    if (!props.dashboardDatasetId || !queries || queries.length === 0) {
      return true;
    }
    return queries.some(q => q.selectedDatasetId || q.queryInfo?.dataset?.id );
  }, [props.dashboardDatasetId, queries]);

  const fixedDatasetId = useMemo(() => {
    if (predefinedDatasetId) {
      return predefinedDatasetId;
    }
    if (useDashboardDataset && props.dashboardDatasetId && !dashboardDatasetOptionIsDisabled) {
      return props.dashboardDatasetId;
    }
    if (queries && queries.length > 0) {
      const dataset = queries[0].selectedDatasetId || queries[0].queryInfo?.dataset?.id;
      return dataset;
    }
    return undefined;
  }, [predefinedDatasetId, useDashboardDataset, props.dashboardDatasetId, dashboardDatasetOptionIsDisabled, queries]);

  const canSubmit = useMemo(() => {
    if (!queries || queries.length === 0) {
      return false;
    }
    // Ensure all queries have a dataset selected or we have a fixed dataset
    const allHaveDatasets = queries.every((query) => {
      return !!(query.selectedDatasetId || query.queryInfo?.dataset?.id || predefinedDatasetId || fixedDatasetId);
    });

    // Ensure any quick search or AI queries have content
    const allHaveContent = queries.every((query) => {
      if (query.savedQueryId) {
        return true;
      }
      if (query.usingAIQueryBuilder) {
        return !!(query.quickSearchContent && query.quickSearchContent.trim().length > 0);
      }
      return !!(query.quickSearchContent && query.quickSearchContent.trim().length > 0);
    });

    return allHaveDatasets && allHaveContent;
  }, [queries, predefinedDatasetId, fixedDatasetId]);

  const activeQueries = queries.filter((templateQuery) => {
    return !!templateQuery.quickSearchContent || templateQuery.savedQueryId || !!templateQuery.aiQueryBuilderQuery;
  });
  const availableQuerySlots = maxWidgetQueries - activeQueries.length;

  return (
    <ResponsiveDialog
      maxWidth="lg"
      open={true}
      onClose={() => props.onClose()}>
      <DialogTitle>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto'}}>
          <Typography sx={{pt:1}} variant="h4">
            {i18n.t("widget-settings.widget-query")}
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
          {(!queries || queries.length < maxWidgetQueries) && (
            <>
            <Button
              variant="outlined"
              onClick={() => setLoadQueryDialogIsOpen(true)}
              size="small" sx={{mr:1}}>
                {i18n.t('widget-types.template-search.load-saved-query')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const newTemplateQuery = new TemplateQueryProps(user);
                setQueries([...(queries || []), newTemplateQuery]);
              }}
              size="small">
                {i18n.t('widget-types.template-search.add-query')}
            </Button>
            </>
          )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "background.default", p: 0}}>
        {error && (
          <Alert severity="error" sx={{mb:2}}>
            {error.server_message}
          </Alert>
        )}
        <WidgetMultiqueryPanel
          queries={queries}
          fixedDatasetId={allowUnmatchedDatasets ? undefined : fixedDatasetId}
          dashboardDatasetId={props.dashboardDatasetId}
          maxWidgetQueries={maxWidgetQueries}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          setQueries={queries => setQueries(queries)} />
        {loadQueryDialogIsOpen && (
          <SavedQueryDialog
            isOpen={true}
            fixedDatasetId={fixedDatasetId}
            showSelectAllButton={true}
            allowMultipleSelection={true}
            maximumSelectedQueries={availableQuerySlots}
            onSubmitAll={(newQueries:Query[]) => {
              let localTemplateQueries = queries;
              // add the new queries to the form, up to the maximum allowed
              newQueries.forEach((query) => {
                if (localTemplateQueries.length < maxWidgetQueries) {
                  // look for a blank query and remove one if found
                  const emptyQuery = localTemplateQueries.find((tq) => !tq.quickSearchContent && !tq.savedQueryId && !tq.aiQueryBuilderQuery);
                  if (emptyQuery) {
                    localTemplateQueries = localTemplateQueries.filter((tq) => tq !== emptyQuery);
                  }
                  // add the query to the form
                  localTemplateQueries = [...localTemplateQueries, { queryTitle: query?.name, quickSearchContent: "", savedQueryId: query.id }];
                }
              });
              setQueries(localTemplateQueries);
              setLoadQueryDialogIsOpen(false);
            }}
            onSubmit={(submittedQueries: Query[]) => {
              const newTemplateQueries = submittedQueries.map((query) => {
                const newTemplateQuery = new TemplateQueryProps(user);
                newTemplateQuery.savedQueryId = query.id;
                newTemplateQuery.queryTitle = query.name;
                newTemplateQuery.usingAIQueryBuilder = false;
                return newTemplateQuery;
              });

              // look for a blank query and remove one if found
              const emptyQuery = queries.find((query) => !query.quickSearchContent && !query.savedQueryId && !query.aiQueryBuilderQuery);
              let newQueries = [...(queries || [])];
              if (emptyQuery) {
                newQueries = newQueries.filter((query) => query !== emptyQuery);
              }
              newQueries.push(...newTemplateQueries);

              setQueries(newQueries);
              setLoadQueryDialogIsOpen(false);
              return newTemplateQueries;
            }}
            onClose={() => setLoadQueryDialogIsOpen(false)}
          />
        )}

      </DialogContent>
      <DialogActions>
        <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: 'auto auto' }}>
          <Box sx={{pl:1}}>
            {(!!props.dashboardDatasetId && !predefinedDatasetId) && (
              <FormControlLabel
                label={i18n.t('widgets.query-form.use-dashboard-dataset')}
                disabled={dashboardDatasetOptionIsDisabled}
                control={
                  <Checkbox
                    checked={useDashboardDataset || (!!fixedDatasetId && fixedDatasetId === props.dashboardDatasetId)}
                    onChange={(event) => {
                      setUseDashboardDataset(event.target.checked);
                    }}/>
              }/>
            )}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Button
              sx={{ mr: 1 }}
              variant="outlined"
              color="error"
              onClick={() => {
                onSubmit([]);
              }}>
              {i18n.t('widgets.query-form.remove-all-widget-queries')}
            </Button>
            <Button
              sx={{ mr: 1 }}
              variant="outlined"
              disabled={queries.length >= maxWidgetQueries}
              onClick={() => onClose()}>
              {i18n.t('cancel')}
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={!props.canSubmitEmptyForm && !canSubmit}
              onClick={() => commitQueries()}
            >
              {i18n.t('submit')}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </ResponsiveDialog>
  );
}